/**
 * Meta Pixel (Facebook/Instagram Ads) — conversion signal for future Meta ad
 * campaigns. Same shape as redditPixel.ts: loads ONLY in production builds AND
 * only for non-internal browsers (same localStorage flag GA4's internal-traffic
 * exclusion uses), so founder/QA walks never train Meta's optimizer.
 *
 * INERT by default: without VITE_META_PIXEL_ID set, `enabled()` is false, so
 * no script tag is injected, no fbq queue is created, and no event ever fires.
 * Set VITE_META_PIXEL_ID in Vercel (+ redeploy) once a Meta ad account exists.
 *
 * Standard event we send: PageView (on init). Custom event: diagnostic_started,
 * fired at the SAME trigger point as GA4's diagnostic_started event (see
 * TryAQuestion.tsx `start()`), so a future Meta campaign is measurable against
 * the identical funnel step GA4 already tracks.
 */
import { isInternalTraffic } from './internalTraffic';
import { isPrerendering } from './prerender';
import { sha256HexNormalizedEmail } from './leadRef';

/**
 * SHA-256 hex of the trimmed, lowercased email — the value Meta expects for
 * advanced matching (`em`) and the stem the backend CAPI uses in the Lead
 * event_id. Delegates to the ONE canonical hash (leadRef) so the pixel `em`,
 * the backend leadId, and the CAPI event_id are byte-for-byte identical.
 */
export function hashEmail(email: string): Promise<string> {
  return sha256HexNormalizedEmail(email);
}

type FbqFn = ((...args: unknown[]) => void) & {
  callMethod?: unknown;
  queue?: unknown[][];
  loaded?: boolean;
  version?: string;
  push?: FbqFn;
};

function fbq(): FbqFn | null {
  if (typeof window === 'undefined') return null;
  const w = window as unknown as { fbq?: FbqFn };
  if (!w.fbq) {
    const queued: FbqFn = ((...args: unknown[]) => {
      if (queued.callMethod) {
        (queued.callMethod as (...a: unknown[]) => void).apply(queued, args);
      } else {
        queued.queue?.push(args);
      }
    }) as FbqFn;
    queued.queue = [];
    queued.loaded = true;
    queued.version = '2.0';
    queued.push = queued;
    w.fbq = queued;
  }
  return w.fbq;
}

let initialized = false;

/** True when the pixel should run at all: an ID is configured, prod build, real visitor. */
function enabled(): boolean {
  return (
    Boolean(import.meta.env.VITE_META_PIXEL_ID) &&
    import.meta.env.PROD &&
    !isPrerendering() &&
    !isInternalTraffic()
  );
}

/**
 * Call once at app boot (after internal-traffic flag handling). No-op if
 * VITE_META_PIXEL_ID is unset. When a hashed email is known (SHA-256 hex, from
 * `hashEmail`), pass it so the pixel initializes with Meta advanced matching
 * (`em`), which raises match rate and lets Meta dedup against the CAPI.
 */
export function initMetaPixel(hashedEmail?: string): void {
  if (initialized || !enabled()) return;
  const pixelId = import.meta.env.VITE_META_PIXEL_ID;
  const f = fbq();
  if (!f || !pixelId) return;
  initialized = true;
  if (hashedEmail) {
    f('init', pixelId, { em: hashedEmail });
  } else {
    f('init', pixelId);
  }
  f('track', 'PageView');
  const s = document.createElement('script');
  s.src = 'https://connect.facebook.net/en_US/fbevents.js';
  s.async = true;
  document.head.appendChild(s);
}

/**
 * Fire a Meta custom event (e.g. the diagnostic funnel steps). No-op until
 * initialized. `eventID` (when given) is passed as the pixel's SEPARATE 4th-arg
 * options object — NOT inside params — so Meta can dedup this browser event
 * against the matching server CAPI event.
 */
export function trackMetaEvent(
  event: string,
  params: Record<string, unknown> = {},
  eventID?: string,
): void {
  if (!initialized || !enabled()) return;
  if (eventID) {
    fbq()?.('trackCustom', event, params, { eventID });
  } else {
    fbq()?.('trackCustom', event, params);
  }
}

/**
 * Fire a Meta STANDARD event (Lead, Purchase, CompleteRegistration, ViewContent)
 * via `track` — NOT `trackCustom` — so Meta's optimizer recognizes it as a real
 * conversion and can optimize/report against it. Use this at the money moments
 * (email captured = Lead, checkout success = Purchase). No-op until the pixel is
 * initialized (VITE_META_PIXEL_ID set, prod build, non-internal visitor).
 */
export function trackMetaStandardEvent(
  event: 'Lead' | 'Purchase' | 'CompleteRegistration' | 'ViewContent',
  params: Record<string, unknown> = {},
  eventID?: string,
): void {
  if (!initialized || !enabled()) return;
  // eventID is Meta's dedup key and MUST be the 4th-arg options object, not a
  // param — the SDK reads `{eventID}` there to match the server CAPI event.
  if (eventID) {
    fbq()?.('track', event, params, { eventID });
  } else {
    fbq()?.('track', event, params);
  }
}
